import React, { useState, useEffect } from "react";
import {
  Drawer,
  Button,
  Text,
  Title,
  List,
  Group,
  Loader,
  Badge,
  Center,
  ThemeIcon,
  Divider,
  Modal,
} from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import useStartupStore from "../stores/startupStore";
import config from "../components/Lib/environment";
import fetchApi from "../components/Lib/api";
import trackAiUsage from "../components/Lib/trackAiUsage";
import Mixpanel from "../components/Lib/mixpanel";
import { useTranslation } from "react-i18next";
import { t } from "i18next";

// Existing renderBlock function remains the same
const renderBlock = (block, index) => {
  switch (block.type) {
    case "header":
      return (
        <Title order={block.data.level || 3} key={index} mt="sm">
          {block.data.text}
        </Title>
      );
    case "paragraph":
      return (
        <Text key={index} mt="sm">
          {block.data.text}
        </Text>
      );
    case "list":
      return block.data.style === "ordered" ? (
        <ol key={index} style={{ marginTop: "0.5rem" }}>
          {block.data.items.map((item, idx) => (
            <li key={idx}>
              <Text>{item}</Text>
            </li>
          ))}
        </ol>
      ) : (
        <ul key={index} style={{ marginTop: "0.5rem" }}>
          {block.data.items.map((item, idx) => (
            <li key={idx}>
              <Text>{item}</Text>
            </li>
          ))}
        </ul>
      );
    default:
      return (
        <Text key={index} mt="sm">
          {block.data.text || JSON.stringify(block)}
        </Text>
      );
  }
};

const OutcomeValidation = () => {
  // Existing state and store retrieval remains the same
  const {
    project_description,
    location,
    step_name,
    subcategories,
    selectedProgram,
    content,
    slug,
    isMember,
    isOwner,
  } = useStartupStore((state) => ({
    slug: state.slug,
    isMember: state.isMember,
    isOwner: state.isOwner,
    project_description: state.startup.description,
    location: state.startup.city + " " + state.startup.country,
    step_name: state.program.categories[state.selectedProgram.cid].name,
    subcategories: Object.values(state.content[state.selectedProgram.cid]).map(
      (sub) => ({
        id: sub.id,
        name: sub.name,
        outcome: JSON.stringify(sub.response),
      })
    ),
    selectedProgram: state.selectedProgram,
    content: state.content,
  }));

  const currentSub = content[selectedProgram.cid]?.[selectedProgram.sid] || {};
  const currentSubName = currentSub.name;
  const currentSubId = currentSub.id;
  const outcomeOfCurrentSub = JSON.stringify(currentSub.response);

  const [validationResult, setValidationResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [streamedData, setStreamedData] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // New state for modals
  const [validationModalOpened, setValidationModalOpened] = useState(false);
  const [historyModalOpened, setHistoryModalOpened] = useState(false);
  const {t}=useTranslation();

  // Existing fetch validation history logic
  useEffect(() => {
    const fetchValidationHistory = async () => {
      if (!currentSubId) return;
      try {
        const response = await fetchApi(
          `/sub_categories/${currentSubId}/validation_history`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch validation history");
        }
        const data = await response.json();
        setHistory(data.validation_history || []);
      } catch (err) {
        console.error(err.message);
      }
    };

    if (historyModalOpened) {
      fetchValidationHistory();
    }
  }, [historyModalOpened, currentSubId, slug]);

  // Existing store validation history method
  const storeValidationHistory = async (result) => {
    if (!currentSubId) return;
    try {
      const response = await fetchApi(
        `/sub_categories/${currentSubId}/validation_history`,
        "POST",
        { validation_result: result }
      );
      if (!response.ok) {
        throw new Error("Failed to store validation history");
      }
    } catch (err) {
      console.error(err.message);
    }
  };

  // Existing validate outcome method
  const validateOutcome = async () => {
    setLoading(true);
    setError(null);
    setStreamedData("");
    let startTime = null;
    try {
      await trackAiUsage("sense_check");
      const payload = {
        project_description,
        location,
        step_name,
        subcategories,
        current_subcategory: currentSubName,
        outcome_of_current_subcategory: outcomeOfCurrentSub,
      };

      startTime = performance.now();

      const response = await fetch(config.validate_url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to validate outcome");
      }

      // Handle streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        fullResponse += chunk;
        setStreamedData(fullResponse);
      }

      const responseTime = Math.round(performance.now() - startTime);
      // Parse the full response JSON
      const data = JSON.parse(fullResponse);
      console.log("Validation result:", data);
      setValidationResult(data);

      Mixpanel.content('Outcome Validation Result', 'Received', {
        subcategory_id: currentSubId,
        subcategory_name: currentSubName,
        rating: data.rating,
        response_time_ms: responseTime,
        startup_slug: slug
    });
      // Store the new validation in the backend
      await storeValidationHistory(data);
      setValidationModalOpened(true);
    } catch (err) {
      const responseTime = startTime ? Math.round(performance.now() - startTime) : null;
      setError(err.message || t("sense_check.errors.limit_reached"));
      setValidationModalOpened(true);
      Mixpanel.error('Outcome Validation Error', {
        error_message: err.message,
        error_type: 'API Error', // Or Network Error depending on the catch
        component: 'OutcomeValidation',
        user_action: 'Perform Sense Check',
        subcategory_id: currentSubId,
        subcategory_name: currentSubName,
        startup_slug: slug,
        response_time_ms: responseTime
      });
    } finally {
      setLoading(false);
    }
  };
  
  if (!(isOwner || isMember)) return <></>;

  return (
    <div>
      <Group spacing="sm">
        <Button
          disabled={loading}
          color="green"
          size="compact-xs" variant="light"
          onClick={validateOutcome}
        >
          {loading ? <Loader size="xs" color="green" /> :t("startups_page.sense_check")}
        </Button>
      </Group>

      {/* Validation Result Modal */}
      <Modal
        opened={validationModalOpened}
        onClose={() => setValidationModalOpened(false)}
        title={
          <Title
            order={3}
            align="center"
            style={{ fontWeight: 600, color: "green" }}
          >
            {error ? "Error" : `Validation Result - ${currentSubName}`}
          </Title>
        }
        size="xl"
      >
        <div style={{ padding: "1.5rem" }}>
          {error ? (
            <Text align="center" style={{ color: "green" }}>
              {error}
            </Text>
          ) : (
            <>
              {validationResult ? (
                <>
                  <Center mb="md">
                    <Badge
                      color="green"
                      size="xl"
                      radius="xl"
                      style={{
                        fontSize: "1.5rem",
                        padding: "1rem 2rem",
                        fontWeight: "bold",
                      }}
                    >
                      Rating: {validationResult.rating}
                    </Badge>
                  </Center>
                  <Text
                    weight={500}
                    size="lg"
                    mb="sm"
                    align="center"
                    style={{ color: "green" }}
                  >
                    Improvements:
                  </Text>
                  <div
                    style={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      padding: "1rem",
                      background: "#f9f9f9",
                      borderRadius: "8px",
                    }}
                  >
                    {validationResult.improvements &&
                    validationResult.improvements.blocks ? (
                      validationResult.improvements.blocks.map((block, index) =>
                        renderBlock(block, index)
                      )
                    ) : (
                      <List
                        spacing="sm"
                        size="sm"
                        icon={
                          <ThemeIcon color="green" size={24} radius="xl">
                            <IconCheck size={16} />
                          </ThemeIcon>
                        }
                      >
                        {validationResult.improvements &&
                          validationResult.improvements.map((imp, idx) => (
                            <List.Item key={idx}>{imp}</List.Item>
                          ))}
                      </List>
                    )}
                  </div>
                  <Center mt="md">
                    <Button 
                      color="green" 
                      variant="outline"
                      onClick={() => {
                        setValidationModalOpened(false);
                        setHistoryModalOpened(true);
                      }}
                    >
                      View All Validations
                    </Button>
                  </Center>
                </>
              ) : (
                <Text align="center" style={{ color: "green" }}>
                  {streamedData || "Streaming response..."}
                </Text>
              )}
            </>
          )}
        </div>
      </Modal>

      {/* Validation History Modal */}
      <Modal
        opened={historyModalOpened}
        onClose={() => setHistoryModalOpened(false)}
        title={
          <Title
            order={3}
            align="center"
            style={{ fontWeight: 600, color: "green" }}
          >
            Validation History - {currentSubName}
          </Title>
        }
        size="xl"
      >
        <div style={{ padding: "1.5rem" }}>
          {history && history.length > 0 ? (
            history.map((entry, idx) => (
              <div 
                key={idx} 
                style={{ 
                  marginBottom: "1.5rem", 
                  border: "1px solid #e0e0e0", 
                  borderRadius: "8px", 
                  padding: "1rem" 
                }}
              >
                <Badge
                  color="green"
                  size="lg"
                  radius="xl"
                  style={{
                    fontSize: "1.2rem",
                    padding: "0.5rem 1rem",
                    fontWeight: "bold",
                    marginBottom: "0.5rem",
                  }}
                >
                  Rating: {entry.rating}
                </Badge>
                <List
                  spacing="sm"
                  size="sm"
                  icon={
                    <ThemeIcon color="green" size={20} radius="xl">
                      <IconCheck size={16} />
                    </ThemeIcon>
                  }
                >
                  {entry.improvements.map((imp, i) => (
                    <List.Item key={i}>{imp}</List.Item>
                  ))}
                </List>
              </div>
            ))
          ) : (
            <Center>
              <Text>No previous validations found.</Text>
            </Center>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default OutcomeValidation;